import React, { useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import { useRouteMatch } from 'react-router-dom';
import { Divider, ListItemButton } from '@mui/material';
import { IAreaToolbarButtonProps } from './area-toolbar-button';
import { joinURL } from '../../../utilities';
import { mainNavigationItems, IMainNavigationChildItem, NavigationGuid } from '../main-navigation/main-navigation-items';

export interface IAreaToolbarProps {
  mainItemGuid: NavigationGuid;
  tools?: React.ReactElement<IAreaToolbarButtonProps>[];
  prefixedText?: string;
  resultTypeDisplayName?: string;
}

const useStyles = makeStyles()((theme: Theme) =>
  ({
    root: {
      backgroundColor: theme.palette.background.paper,
      display: 'inline-flex',
      fontSize: 14,
      fontWeight: 'bolder',
    },
    icon: {
      fontSize: 12,
      marginLeft: theme.spacing(1),
    },
    childIcon: {
      display: 'inline-block',
      width: theme.spacing(4),
    },
    listRoot: {
      display: 'inline-block',
    },
    listItemRoot: {
      display: 'inline-flex',
      width: 'auto',
    },
    toolsDivider: {
      height: '60%',
      alignSelf: 'center',
      marginRight: theme.spacing(1),
    },
  })
);

function getMainNavigationItemByGuid(guid: string) {
  return mainNavigationItems.find(item => item.guid === guid);
}

export function AreaToolbar(props: IAreaToolbarProps) {
  const { classes } = useStyles();
  const match = useRouteMatch();
  const { mainItemGuid, tools, prefixedText = '', resultTypeDisplayName } = props;
  const [selectedOption, setSelectedOption] = React.useState<IMainNavigationChildItem | undefined>();

  const mainItem = getMainNavigationItemByGuid(mainItemGuid);

  useEffect(() => {
    const children = mainItem && mainItem.children ? mainItem.children : [];
    const currUrl = joinURL(match.url);

    const findSelected = children.length
      ? children.reduce((acc: IMainNavigationChildItem, curr: IMainNavigationChildItem) => {
        if (curr.url) {
          let url = joinURL('/', mainItem ? mainItem.url : '', curr.url);
          if (curr.useDirectUrl) {
            url = joinURL('/', curr.url);
          }
          if (url === currUrl) return curr;
        }
        return acc;
        })
      : undefined;
    setSelectedOption(findSelected);
  }, [mainItemGuid, match.url, mainItem]);

  let toolsArr: React.ReactElement<IAreaToolbarButtonProps>[] = [];

  if (tools) {
    toolsArr = tools
      .map((tool: React.ReactElement<IAreaToolbarButtonProps>, i: number) => {
        const keyed = { ...tool, key: `toolbar_${i}` };
        return keyed;
      })
      .filter((tool: React.ReactElement<IAreaToolbarButtonProps>) => !tool.props.showOn);
  }

  function postFixedText() {
    if (resultTypeDisplayName) {
      return resultTypeDisplayName;
    }
    return selectedOption ? selectedOption.name : 'Intet valgt';
  }

  return (
    <div id="areaToolbarDiv" className={classes.root}>
      <List component="nav" aria-label="Sub area navigation" classes={{ root: classes.listRoot }} disablePadding>
        <ListItemButton aria-haspopup="true" aria-controls="sub-area-menu" aria-label="selected sub area" classes={{ root: classes.listItemRoot }}>
          <ListItemText disableTypography>
            {prefixedText}
            {postFixedText()}
          </ListItemText>
        </ListItemButton>
      </List>
      {tools && (
        <>
          <Divider orientation="vertical" className={classes.toolsDivider} />
          {toolsArr}
        </>
      )}
    </div>
  );
}
